import { useMemo } from "react";
import {
  AssessmentQuestionComment,
  AssessmentType,
  CalibrationNodeFragment,
} from "types/graphql-schema";

import { isEmptyValue } from "@components/wysiwyg/helpers";
import TextareaWysiwyg from "@components/wysiwyg/textarea-wysiwyg";

import { getAssessmentQuestionText } from "../assessment-page-context";
import AssessmentQuestionRangeOptions from "../assessment-question-range-options";
import QuestionContextButton from "./question-context-button";
import SavedAnswerEditorKey from "./saved-answer-editor-key";
import {
  AssessmentAnswer,
  AssessmentAnswerWithResponderAndId,
  AssessmentQuestion,
} from "./types";

const RangeAssessmentQuestionContent = ({
  answer,
  otherParticipantsAnswers,
  question,
  disabled,
  onlyRangeOptionsDisabled,
  calibrations,
  forceHideComment = false,
  onChangeAnswer,
  assessmentType,
}: {
  answer?: { integerAnswer?: number | null; comment?: string | null } | null;
  otherParticipantsAnswers?: AssessmentAnswerWithResponderAndId[];
  forceHideComment?: boolean;
  question: Extract<AssessmentQuestion, { startValue: number }>;
  disabled?: boolean;
  onlyRangeOptionsDisabled?: boolean;
  calibrations?: CalibrationNodeFragment[];
  onChangeAnswer?: (newAnswer: Partial<AssessmentAnswer>) => void;
  assessmentType?: AssessmentType | null;
}) => {
  const comment = answer?.comment ?? "{}";

  const initialAnswer = useMemo(() => {
    const filteredCalibrations = calibrations?.filter(
      (calibration) => !calibration.isSuggestion,
    );
    if (!filteredCalibrations || filteredCalibrations.length === 0) {
      return answer?.integerAnswer;
    }
    return filteredCalibrations[filteredCalibrations.length - 1]
      .prevIntegerAnswer;
  }, [calibrations, answer]);

  return (
    <>
      <AssessmentQuestionRangeOptions
        answer={answer?.integerAnswer}
        answerHasChanged={answer?.integerAnswer !== initialAnswer}
        otherParticipantsAnswers={otherParticipantsAnswers}
        disabled={disabled || onlyRangeOptionsDisabled}
        labels={question.labels}
        labelDescriptions={question.labelDescriptions}
        startValue={question.startValue}
        endValue={question.endValue}
        onClickAnswer={(answer) => {
          if (!onChangeAnswer) {
            return;
          }
          onChangeAnswer({
            integerAnswer: answer,
          });
        }}
      />
      {!forceHideComment &&
        question.comment !== AssessmentQuestionComment.NoComment &&
        answer &&
        (!disabled || !isEmptyValue(answer.comment)) && (
          <div className="mt-4 flex flex-col gap-2">
            <div className="flex items-center justify-between">
              <div className="text-gray-500 text-xs uppercase font-semibold">
                Comment{" "}
                {question.comment === AssessmentQuestionComment.Required
                  ? "*"
                  : ""}
              </div>
            </div>
            <SavedAnswerEditorKey field="comment">
              <TextareaWysiwyg
                editable={!disabled}
                placeholder="Add a comment..."
                className="bg-white max-w-none"
                value={comment}
                onChangeValue={(comment) => {
                  if (!onChangeAnswer) {
                    return;
                  }
                  onChangeAnswer({
                    comment,
                  });
                }}
              />
            </SavedAnswerEditorKey>
            {!disabled && (
              <QuestionContextButton
                title={getAssessmentQuestionText(question).title}
                questionId={question.id}
                assessmentType={assessmentType}
              />
            )}
          </div>
        )}
    </>
  );
};

export default RangeAssessmentQuestionContent;
